import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SettingsView } from "../components/SettingsView.js";
import { ApiClientError, getMe } from "../lib/api.js";
import { getPublicSettings, setResearchKillSwitch } from "../lib/settings.js";

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => getMe(),
  });
  const isAdmin = meQuery.data?.role === "admin";

  const settingsQuery = useQuery({
    queryKey: ["settings", "public"],
    queryFn: () => getPublicSettings(),
  });

  const mutation = useMutation({
    mutationFn: (enabled: boolean) => setResearchKillSwitch(enabled),
    onSuccess: async (setting) => {
      setActionError(null);
      setActionMessage(
        setting.enabled
          ? "Kill Switch aktiviert."
          : "Kill Switch deaktiviert.",
      );
      setConfirmOpen(false);
      setPendingEnabled(null);
      await queryClient.invalidateQueries({ queryKey: ["settings", "public"] });
    },
    onError: (err) => {
      setActionMessage(null);
      setActionError(
        err instanceof ApiClientError
          ? err.message
          : "Speichern fehlgeschlagen.",
      );
    },
  });

  const state =
    settingsQuery.isLoading || meQuery.isLoading
      ? "loading"
      : settingsQuery.isError
        ? "error"
        : "ready";

  return (
    <SettingsView
      state={state}
      errorMessage={
        settingsQuery.error instanceof ApiClientError
          ? settingsQuery.error.message
          : settingsQuery.error
            ? "Settings konnten nicht geladen werden."
            : null
      }
      actionError={actionError}
      actionMessage={actionMessage}
      killSwitchEnabled={Boolean(settingsQuery.data?.research_kill_switch)}
      isAdmin={isAdmin}
      busy={mutation.isPending}
      confirmOpen={confirmOpen}
      pendingEnabled={pendingEnabled}
      onToggleRequest={(next) => {
        setActionError(null);
        setActionMessage(null);
        setPendingEnabled(next);
        setConfirmOpen(true);
      }}
      onConfirm={() => {
        if (pendingEnabled == null) return;
        mutation.mutate(pendingEnabled);
      }}
      onCancelConfirm={() => {
        setConfirmOpen(false);
        setPendingEnabled(null);
      }}
      onRetry={() => {
        void settingsQuery.refetch();
        void meQuery.refetch();
      }}
    />
  );
}
